<template>
  <div class="route-planner-page" :class="{ 'route-planner-page--embedded': embedded }">
    <div ref="mapEl" class="route-planner-map"></div>

    <section class="planner-panel" :class="{ 'planner-panel--collapsed': panelCollapsed }">
      <template v-if="!panelCollapsed">
        <div class="planner-panel__head">
          <strong>{{ isReadOnly ? '路线详情' : '路线配置' }}</strong>
          <el-button v-if="!isReadOnly" type="primary" plain size="small" icon="el-icon-plus" :disabled="form.waypoints.length >= 8" @click="addWaypoint">途经点</el-button>
        </div>

        <div class="planner-panel__nodes">
          <div class="planner-node planner-node--origin">
            <span class="planner-node__marker">起</span>
            <span class="planner-node__address" :class="{ 'planner-node__address--empty': !form.start.address }">{{ plannerAddressText(form.start, '起点') }}</span>
            <el-button v-if="!isReadOnly" type="primary" size="mini" icon="el-icon-map-location" @click="openLocationPicker(form.start)">选择</el-button>
          </div>
          <div v-for="(node, index) in form.waypoints" :key="node.key" class="planner-node planner-node--waypoint">
            <span class="planner-node__marker">{{ index + 1 }}</span>
            <span class="planner-node__address" :class="{ 'planner-node__address--empty': !node.address }">{{ plannerAddressText(node, `途经点 ${index + 1}`) }}</span>
            <el-button v-if="!isReadOnly" type="primary" size="mini" icon="el-icon-map-location" @click="openLocationPicker(node)">选择</el-button>
            <span v-if="!isReadOnly" class="planner-node__tools">
              <i class="el-icon-caret-top" :class="{ 'planner-node__tool--disabled': index === 0 }" @click="index > 0 && moveWaypoint(index, -1)"></i>
              <i class="el-icon-caret-bottom" :class="{ 'planner-node__tool--disabled': index === form.waypoints.length - 1 }" @click="index < form.waypoints.length - 1 && moveWaypoint(index, 1)"></i>
              <i class="el-icon-remove-outline" @click="removeWaypoint(index)"></i>
            </span>
          </div>
          <div class="planner-node planner-node--target">
            <span class="planner-node__marker">终</span>
            <span class="planner-node__address" :class="{ 'planner-node__address--empty': !form.end.address }">{{ plannerAddressText(form.end, '终点') }}</span>
            <el-button v-if="!isReadOnly" type="primary" size="mini" icon="el-icon-map-location" @click="openLocationPicker(form.end)">选择</el-button>
          </div>
        </div>

        <p v-if="missingSavedRoute" class="planner-route-warning" role="alert">该路线未保存规划线路，当前仅显示起终点和途经点，请编辑后重新规划并保存</p>
        <div v-if="isReadOnly" class="planner-readonly-info">
          <div><span>路线名称</span><strong>{{ form.routeName || '-' }}</strong></div>
          <div><span>驾车策略</span><strong>{{ strategyText(form.drivingStrategy) }}</strong></div>
          <div><span>规划里程</span><strong>{{ mileageText(form.totalDileage) }}</strong></div>
          <div><span>预计时长</span><strong>{{ durationText(form.totalDuration) }}</strong></div>
          <div><span>路线收费</span><strong>{{ form.tolls === null || form.tolls === undefined ? '-' : `${Number(form.tolls).toFixed(2)} 元` }}</strong></div>
        </div>
        <div v-else class="planner-panel__form">
          <el-select v-model="form.drivingStrategy" placeholder="选择驾车策略" class="planner-field" @change="clearPlan">
            <el-option label="速度优先" :value="0" />
            <el-option label="最少费用" :value="1" />
            <el-option label="最短路径" :value="2" />
            <el-option label="规避拥堵" :value="3" />
          </el-select>
          <el-input v-if="!embedded" v-model.trim="form.routeName" maxlength="100" class="planner-field" placeholder="请输入路线名称（例如：日常运输路线）" />
          <div class="planner-panel__actions">
            <el-button type="text" @click="clearPlan">清除路线</el-button>
            <el-button type="primary" size="small" :loading="planning" @click="planRoute">规划</el-button>
          </div>
        </div>

        <template v-if="!isReadOnly">
          <div v-if="planRoutes.length" class="candidate-list">
            <div class="candidate-title">推荐路线</div>
            <button v-for="(route, index) in planRoutes" :key="route.key" class="candidate-item" :class="{ 'candidate-item--selected': selectedPlanIndex === index }" @click="selectPlan(index)">
              <span class="candidate-item__name">{{ route.strategyName }}<em v-if="index === 0">推荐</em></span>
              <span>{{ durationText(route.totalDuration) }} · {{ mileageText(route.totalDileage) }}</span>
              <span v-if="route.tolls !== null">收费 {{ Number(route.tolls).toFixed(2) }} 元</span>
              <span class="candidate-item__action">{{ selectedPlanIndex === index ? '已选择' : '选择此路线' }}</span>
            </button>
            <div class="planner-drag-tip">{{ form.routeList ? '已突出显示所选方案；开启拖动调整后可修改线路。' : '已展示全部候选，请点击路线或左侧方案进行选择。' }}</div>
          </div>
          <div v-else-if="form.routeList" class="planner-summary">已选择：{{ durationText(form.totalDuration) }} · {{ mileageText(form.totalDileage) }}</div>
          <div v-else class="planner-empty">选择起点、终点后即可规划；途经点为可选。</div>
          <el-button v-if="form.routeList" type="text" size="small" class="planner-drag-action" @click="startRouteDragging">开启拖动调整</el-button>
        </template>

        <div v-if="!embedded" class="planner-panel__footer">
          <template v-if="isReadOnly">
            <el-button @click="backToList">返回列表</el-button>
          </template>
          <template v-else>
            <el-button @click="returnToConfig">返回配置</el-button>
            <el-button type="primary" :disabled="!form.routeList" v-permission="form.id ? 'route:edit' : 'route:add'" @click="submit">选择路线</el-button>
          </template>
        </div>
      </template>
    </section>

    <button class="planner-collapse" :class="{ 'planner-collapse--closed': panelCollapsed }" @click="panelCollapsed = !panelCollapsed">
      <i :class="panelCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-left'"></i>
    </button>
    <el-button v-if="!embedded" class="planner-back" icon="el-icon-back" @click="isReadOnly ? backToList() : returnToConfig()">{{ isReadOnly ? '返回列表' : '返回路线配置' }}</el-button>

    <div class="map-legend"><span><i class="map-dot map-dot--start"></i>起点</span><span><i class="map-dot map-dot--waypoint"></i>途经点</span><span><i class="map-dot map-dot--end"></i>终点</span></div>

    <waybill-location-picker v-if="!isReadOnly" :visible.sync="locationPickerVisible" :lon-lat="selectedNode ? selectedNode.lonLat : ''" :address="selectedNode ? selectedNode.address : ''" :area-codes="selectedNode ? selectedNode.areaCodes : []" :area-name="selectedNode ? getAreaName(selectedNode) : ''" @confirm="applyPickedLocation" />
  </div>
</template>

<script>
import WaybillLocationPicker from './WaybillLocationPicker.vue'
import { setAmapSecurityConfig, loadAmapSDK } from '@/utils/amapConfig'
import { getRouteDetail, isRequestSuccess } from './lingdongApi'
import { normalizeAreaCodes, getAreaName, stripAreaPrefix, buildFullAddress, getAreaCodesByAdcode, getDetailAddress } from './locationArea'
import { parseLonLat, gcj02PointToWgs84LonLat, gcj02RouteToWgs84, wgs84LonLatToGcj02Point, wgs84RouteToGcj02 } from './coordinate'

const ROUTE_DRAFT_KEY = 'lingdong-route-draft'
const createNode = () => ({ key: `route-node-${Date.now()}-${Math.random()}`, areaCodes: [], address: '', lonLat: '', contactName: '', contactPhone: '', radius: 500, selectedAreaName: '' })
const createForm = () => ({ id: null, routeName: '', drivingStrategy: 0, status: 1, start: createNode(), end: createNode(), waypoints: [], routeList: '', totalDileage: null, totalDuration: null, tolls: null, tollDistance: null })

export default {
  name: 'RouteForm',
  components: { WaybillLocationPicker },
  props: { embedded: { type: Boolean, default: false }, initialForm: { type: Object, default: () => ({}) } },
  data() {
    return {
      planning: false,
      planRequestSequence: 0,
      mapDisposed: false,
      firstActivated: true,
      panelCollapsed: false,
      locationPickerVisible: false,
      selectedNode: null,
      form: createForm(),
      map: null,
      dragRoute: null,
      planRoutes: [],
      selectedPlanIndex: -1
    }
  },
  computed: {
    isReadOnly() { return !this.embedded && this.$route.name === 'LingDongRouteViewName' },
    missingSavedRoute() { return this.isReadOnly && String(this.form.routeList || '').split('|').filter(value => parseLonLat(value)).length < 2 }
  },
  watch: {
    form: { deep: true, handler(value) { if (this.embedded) this.$emit('draft-change', JSON.parse(JSON.stringify(value))) } }
  },
  async mounted() {
    const ready = this.isReadOnly ? await this.loadRouteDetail() : this.restoreDraft()
    if (!ready) {
      this.$message.warning(this.isReadOnly ? '路线不存在或无法读取' : '未读取到路线配置，请从路线配置页进入地图规划')
      this.$router.replace({ name: 'LingDongRouteManageName' })
      return
    }
    await this.initMap()
    this.renderSavedRoute()
  },
  async activated() {
    if (this.embedded) return
    if (this.firstActivated) {
      this.firstActivated = false
      return
    }
    const ready = this.isReadOnly ? await this.loadRouteDetail() : this.restoreDraft()
    if (!ready) {
      this.$router.replace({ name: 'LingDongRouteManageName' })
      return
    }
    this.destroyDragRoute()
    this.planRoutes = []
    this.selectedPlanIndex = -1
    await this.$nextTick()
    if (this.map) this.map.resize()
    this.renderSavedRoute()
  },
  beforeDestroy() {
    this.mapDisposed = true
    this.planRequestSequence++
    this.destroyDragRoute()
    if (this.map) this.map.destroy()
  },
  methods: {
    restoreDraft() {
      if (this.embedded) {
        this.form = this.normalizeForm(JSON.parse(JSON.stringify(this.initialForm)))
        return true
      }
      try {
        const value = sessionStorage.getItem(ROUTE_DRAFT_KEY)
        if (!value) return false
        this.form = this.normalizeForm(JSON.parse(value))
        return true
      } catch (e) {
        sessionStorage.removeItem(ROUTE_DRAFT_KEY)
        return false
      }
    },
    async loadRouteDetail() {
      try {
        const res = await getRouteDetail(this.$route.params.id)
        if (!isRequestSuccess(res)) return false
        const data = res.data || {}
        this.form = {
          id: data.id,
          routeName: data.routeName || '',
          drivingStrategy: Number(data.drivingStrategy) || 0,
          status: data.status === undefined || data.status === null ? 1 : Number(data.status),
          start: this.normalizeNode({ areaCodes: data.startAreaCodes, areaName: data.startAreaName, address: data.startAddress, lonLat: data.startLonlat, contactName: data.startContactName, contactPhone: data.startContactPhone, radius: data.startRadius }),
          end: this.normalizeNode({ areaCodes: data.endAreaCodes, areaName: data.endAreaName, address: data.endAddress, lonLat: data.endLonlat, contactName: data.endContactName, contactPhone: data.endContactPhone, radius: data.endRadius }),
          waypoints: this.parseWaypoints(data.waypoints),
          routeList: data.routeList || '',
          totalDileage: data.totalDileage,
          totalDuration: data.totalDuration,
          tolls: data.tolls,
          tollDistance: data.tollDistance
        }
        return true
      } catch (e) {
        return false
      }
    },
    normalizeForm(value) {
      const result = { ...createForm(), ...value }
      result.start = this.normalizeNode(value.start)
      result.end = this.normalizeNode(value.end)
      result.waypoints = Array.isArray(value.waypoints) ? value.waypoints.map(item => this.normalizeNode(item)) : []
      return result
    },
    normalizeNode(node = {}) {
      const result = { ...createNode(), ...node }
      result.areaCodes = normalizeAreaCodes(result.areaCodes)
      result.lonLat = result.lonLat || result.lonlat || ''
      result.radius = Number(result.radius) || 500
      result.selectedAreaName = this.getAreaName(result) || result.areaName || result.selectedAreaName || ''
      result.address = stripAreaPrefix(result.address, result.selectedAreaName)
      return result
    },
    parseWaypoints(value) {
      try {
        const items = JSON.parse(value || '[]')
        return Array.isArray(items) ? items.map(item => this.normalizeNode(item)) : []
      } catch (e) {
        return []
      }
    },
    addWaypoint() {
      if (this.form.waypoints.length >= 8) return
      this.form.waypoints.push(createNode())
      this.clearPlan()
    },
    removeWaypoint(index) { this.form.waypoints.splice(index, 1); this.clearPlan() },
    moveWaypoint(index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.form.waypoints.length) return
      const item = this.form.waypoints[index]
      this.$set(this.form.waypoints, index, this.form.waypoints[target])
      this.$set(this.form.waypoints, target, item)
      this.clearPlan()
    },
    openLocationPicker(node) { this.selectedNode = node; this.locationPickerVisible = true },
    applyPickedLocation(location) {
      if (!this.selectedNode) return
      const changed = !this.isSameLonLat(this.selectedNode.lonLat, location.lonLat)
      this.selectedNode.lonLat = changed ? location.lonLat : this.selectedNode.lonLat
      if (Array.isArray(location.areaCodes) && location.areaCodes.length) this.selectedNode.areaCodes = normalizeAreaCodes(location.areaCodes)
      this.selectedNode.selectedAreaName = this.getAreaName(this.selectedNode) || location.areaName || ''
      if (location.address) this.selectedNode.address = stripAreaPrefix(location.address, this.selectedNode.selectedAreaName)
      this.selectedNode = null
      if (changed) this.clearPlan()
    },
    getAreaName(node) { return getAreaName(node.areaCodes) },
    getFullAddress(node) {
      return buildFullAddress(node.areaCodes, node.address)
    },
    plannerAddressText(node, title) { return node.address || `${title}地址` },
    parseLonLat(value) {
      const values = String(value || '').trim().split(',').map(Number)
      return values.length === 2 && values.every(Number.isFinite) && Math.abs(values[0]) <= 180 && Math.abs(values[1]) <= 90 ? values : null
    },
    validateNode(node, label) {
      if (!this.getFullAddress(node)) return `${label}地址不能为空`
      if (!this.parseLonLat(node.lonLat)) return `${label}坐标格式错误，请返回配置页解析坐标或地图选点`
      return ''
    },
    validatePlanNodes() {
      const startError = this.validateNode(this.form.start, '起点')
      if (startError) return startError
      for (let index = 0; index < this.form.waypoints.length; index += 1) {
        const error = this.validateNode(this.form.waypoints[index], `途经点${index + 1}`)
        if (error) return error
      }
      return this.validateNode(this.form.end, '终点')
    },
    async initMap() {
      try {
        setAmapSecurityConfig()
        await loadAmapSDK(['AMap.Driving', 'AMap.DragRoute', 'AMap.Geocoder', 'AMap.ToolBar'])
        if (this.mapDisposed) return
        this.map = new window.AMap.Map(this.$refs.mapEl, { zoom: 5, center: [108.946, 34.264], resizeEnable: true })
        this.map.addControl(new window.AMap.ToolBar())
      } catch (e) {
        this.$message.error('地图加载失败，请刷新后重试')
      }
    },
    getDrivingStrategies() {
      const policies = window.AMap && window.AMap.DrivingPolicy
      if (!policies) return []
      const getPolicy = (name, fallback) => policies[name] === undefined ? policies[fallback] : policies[name]
      const strategies = [
        { value: 0, name: '速度优先', policy: getPolicy('LEAST_TIME', 'REAL_TRAFFIC') },
        { value: 1, name: '最少费用', policy: getPolicy('LEAST_FEE', 'REAL_TRAFFIC') },
        { value: 2, name: '最短路径', policy: getPolicy('LEAST_DISTANCE', 'REAL_TRAFFIC') },
        { value: 3, name: '规避拥堵', policy: getPolicy('REAL_TRAFFIC', 'LEAST_TIME') }
      ]
      const selected = strategies.find(item => item.value === Number(this.form.drivingStrategy)) || strategies[0]
      return [selected, ...strategies.filter(item => item.value !== selected.value)]
    },
    mapPolicy() {
      const selected = this.getDrivingStrategies()[0]
      return selected ? selected.policy : 0
    },
    queryDrivingRoute(policy, start, end, waypoints) {
      return new Promise((resolve, reject) => {
        const driving = new window.AMap.Driving({ policy })
        const callback = (status, data) => status === 'complete' ? resolve(data) : reject(new Error('未查询到可用路线'))
        if (waypoints.length) driving.search(start, end, { waypoints }, callback)
        else driving.search(start, end, callback)
      })
    },
    async planRoute() {
      const error = this.validatePlanNodes()
      if (error) { this.$message.warning(error); return }
      if (!this.map) { this.$message.warning('地图尚未准备完成'); return }
      const requestId = ++this.planRequestSequence
      this.planning = true
      try {
        const start = wgs84LonLatToGcj02Point(this.form.start.lonLat)
        const end = wgs84LonLatToGcj02Point(this.form.end.lonLat)
        const waypoints = this.form.waypoints.map(node => wgs84LonLatToGcj02Point(node.lonLat))
        const strategyResults = await Promise.all(this.getDrivingStrategies().map(async (strategy) => {
          try {
            const result = await this.queryDrivingRoute(strategy.policy, start, end, waypoints)
            return (result.routes || []).map((route, index) => this.buildPlanRoute(route, index, strategy)).filter(item => item.routeList)
          } catch (e) {
            return []
          }
        }))
        if (requestId !== this.planRequestSequence || this.mapDisposed) return
        const routeKeys = new Set()
        this.planRoutes = strategyResults.flat().filter((route) => {
          if (routeKeys.has(route.routeList)) return false
          routeKeys.add(route.routeList)
          return true
        })
        if (!this.planRoutes.length) throw new Error('未查询到可用路线')
        this.selectedPlanIndex = -1
        this.form.routeList = ''
        this.form.totalDileage = null
        this.form.totalDuration = null
        this.renderMap([])
        this.$message.success('路线规划完成，请选择路线方案')
      } catch (e) {
        if (requestId === this.planRequestSequence && !this.mapDisposed) this.$message.error(e.message || '路线规划失败，请检查节点坐标')
      } finally {
        if (requestId === this.planRequestSequence) this.planning = false
      }
    },
    buildPlanRoute(route, index, strategy = {}) {
      const strategyName = strategy.name || strategy.strategyName || '路线方案'
      const strategyValue = strategy.value === undefined
        ? (strategy.strategyValue === undefined ? Number(this.form.drivingStrategy) : strategy.strategyValue)
        : strategy.value
      const points = []
      ;(route.steps || []).forEach(step => {
        (step.path || []).forEach(point => {
          const lng = typeof point.getLng === 'function' ? point.getLng() : point.lng
          const lat = typeof point.getLat === 'function' ? point.getLat() : point.lat
          if (Number.isFinite(lng) && Number.isFinite(lat)) points.push([Number(lng), Number(lat)])
        })
      })
      const uniquePoints = points.filter((point, pointIndex) => !pointIndex || point[0] !== points[pointIndex - 1][0] || point[1] !== points[pointIndex - 1][1])
      return {
        key: `${Date.now()}-${strategyName}-${index}`,
        strategyName,
        strategyValue,
        policy: strategy.policy === undefined ? this.mapPolicy() : strategy.policy,
        points: uniquePoints,
        routeList: gcj02RouteToWgs84(uniquePoints),
        totalDileage: Number(route.distance || 0) / 1000,
        totalDuration: Number(route.time || 0),
        tolls: route.tolls === undefined ? null : Number(route.tolls),
        tollDistance: route.toll_distance === undefined ? null : Number(route.toll_distance) / 1000
      }
    },
    selectPlan(index) {
      const route = this.planRoutes[index]
      if (!route) return
      this.selectedPlanIndex = index
      this.form.drivingStrategy = route.strategyValue
      this.form.routeList = route.routeList
      this.form.totalDileage = Number(route.totalDileage).toFixed(2)
      this.form.totalDuration = route.totalDuration
      this.form.tolls = route.tolls
      this.form.tollDistance = route.tollDistance === null ? null : Number(route.tollDistance).toFixed(2)
      this.renderMap(route.points || wgs84RouteToGcj02(route.routeList))
    },
    startRouteDragging() {
      const route = this.planRoutes[this.selectedPlanIndex]
      this.enableRouteDragging(route ? route.policy : this.mapPolicy())
    },
    renderSavedRoute() {
      const points = wgs84RouteToGcj02(this.form.routeList)
      this.renderMap(points)
    },
    destroyDragRoute() {
      if (!this.dragRoute) return
      this.dragRoute.destroy()
      this.dragRoute = null
    },
    enableRouteDragging(policy = this.mapPolicy()) {
      const start = wgs84LonLatToGcj02Point(this.form.start.lonLat)
      const end = wgs84LonLatToGcj02Point(this.form.end.lonLat)
      const waypoints = this.form.waypoints.map(node => wgs84LonLatToGcj02Point(node.lonLat)).filter(Boolean)
      if (!this.map || !start || !end || !window.AMap.DragRoute) {
        this.renderMap(wgs84RouteToGcj02(this.form.routeList))
        return
      }
      this.destroyDragRoute()
      this.map.clearMap()
      const points = [start, ...waypoints, end].map(point => new window.AMap.LngLat(point[0], point[1]))
      const dragRoute = new window.AMap.DragRoute(this.map, points, policy)
      this.dragRoute = dragRoute
      dragRoute.on('complete', result => {
        if (this.dragRoute === dragRoute) this.handleDragRouteChange(result, false)
      })
      dragRoute.on('routeChanged', result => {
        if (this.dragRoute === dragRoute) this.handleDragRouteChange(result, true)
      })
      dragRoute.search()
    },
    handleDragRouteChange(result, showMessage) {
      const data = result && result.data ? result.data : result
      const route = data && Array.isArray(data.routes) ? data.routes[0] : null
      if (!route) return
      const selectedPlan = this.planRoutes[this.selectedPlanIndex]
      const plan = this.buildPlanRoute(route, 0, selectedPlan || {})
      if (!plan.routeList) return
      this.form.routeList = plan.routeList
      this.form.totalDileage = Number(plan.totalDileage).toFixed(2)
      this.form.totalDuration = plan.totalDuration
      this.form.tolls = plan.tolls
      this.form.tollDistance = plan.tollDistance === null ? null : Number(plan.tollDistance).toFixed(2)
      this.syncDragWaypoints(data)
      if (this.selectedPlanIndex >= 0 && this.planRoutes[this.selectedPlanIndex]) this.$set(this.planRoutes, this.selectedPlanIndex, plan)
      if (showMessage) this.$message.success('路线已更新，可继续拖动调整')
    },
    syncDragWaypoints(data) {
      if (!data || !Array.isArray(data.waypoints)) return
      const originalWaypoints = this.form.waypoints.slice()
      const sameWaypointCount = data.waypoints.length === originalWaypoints.length
      this.form.waypoints = data.waypoints.map((item, index) => {
        const point = this.getWaypointPoint(item)
        if (!point) return null
        const lonLat = gcj02PointToWgs84LonLat(point[0], point[1])
        const existed = originalWaypoints.find(node => this.isSameLonLat(node.lonLat, lonLat)) || (sameWaypointCount ? originalWaypoints[index] : null)
        if (existed) return { ...existed, lonLat }
        const node = { ...createNode(), lonLat, address: String(item.name || item.address || `途经点 ${index + 1}`).trim() }
        this.resolveWaypointAddress(node)
        return node
      }).filter(Boolean)
    },
    getWaypointPoint(item) {
      const location = item && (item.location || item)
      if (Array.isArray(location)) {
        const values = location.map(Number)
        return values.length === 2 && values.every(Number.isFinite) ? values : null
      }
      const lng = location && (typeof location.getLng === 'function' ? location.getLng() : location.lng)
      const lat = location && (typeof location.getLat === 'function' ? location.getLat() : location.lat)
      return Number.isFinite(Number(lng)) && Number.isFinite(Number(lat)) ? [Number(lng), Number(lat)] : null
    },
    isSameLonLat(left, right) {
      const leftPoint = this.parseLonLat(left)
      const rightPoint = this.parseLonLat(right)
      return leftPoint && rightPoint && Math.abs(leftPoint[0] - rightPoint[0]) < 0.000001 && Math.abs(leftPoint[1] - rightPoint[1]) < 0.000001
    },
    resolveWaypointAddress(node) {
      const point = wgs84LonLatToGcj02Point(node.lonLat)
      if (!point || !window.AMap || !window.AMap.Geocoder) return
      const requestId = this.planRequestSequence
      const lonLat = node.lonLat
      new window.AMap.Geocoder().getAddress(point, (status, result) => {
        if (this.mapDisposed || requestId !== this.planRequestSequence || node.lonLat !== lonLat || !this.form.waypoints.includes(node)) return
        if (status !== 'complete' || !result.regeocode || !result.regeocode.formattedAddress) return
        const component = result.regeocode.addressComponent || {}
        node.areaCodes = getAreaCodesByAdcode(component.adcode)
        node.selectedAreaName = this.getAreaName(node)
        node.address = getDetailAddress(result.regeocode.formattedAddress, component, node.areaCodes)
      })
    },
    renderMap(routePoints) {
      if (!this.map) return
      this.destroyDragRoute()
      this.map.clearMap()
      const overlays = []
      const markerConfig = [
        { node: this.form.start, label: '起', color: '#18a058' },
        ...this.form.waypoints.map((node, index) => ({ node, label: String(index + 1), color: '#409eff' })),
        { node: this.form.end, label: '终', color: '#f56c6c' }
      ]
      markerConfig.forEach(item => {
        const point = wgs84LonLatToGcj02Point(item.node.lonLat)
        if (!point) return
        const marker = new window.AMap.Marker({ position: point, draggable: !this.isReadOnly, content: `<div style="width:30px;height:30px;border-radius:15px;background:${item.color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);color:#fff;text-align:center;line-height:30px;font-size:12px;font-weight:600">${item.label}</div>`, offset: new window.AMap.Pixel(-15, -15) })
        if (!this.isReadOnly) marker.on('dragend', event => this.applyMarkerDrag(item.node, event))
        marker.setMap(this.map)
        overlays.push(marker)
      })
      // 候选预览与保存选择分开，未选定时也展示全部方案，不写入运单线路。
      const routes = this.planRoutes.length
        ? this.planRoutes.map((route, index) => ({ points: route.points || wgs84RouteToGcj02(route.routeList), index }))
        : [{ points: routePoints, index: -1 }]
      const selectedIndex = this.selectedPlanIndex < 0 ? 0 : this.selectedPlanIndex
      routes.forEach(route => {
        if (route.points.length < 2) return
        const selected = route.index === -1 || route.index === selectedIndex
        const line = new window.AMap.Polyline({ path: route.points, strokeColor: selected ? '#1f9d4d' : '#90a4b8', strokeWeight: selected ? 7 : 5, strokeOpacity: selected ? .95 : .65, zIndex: selected ? 60 : 50, lineJoin: 'round', lineCap: 'round' })
        if (route.index >= 0) line.on('click', () => this.selectPlan(route.index))
        line.setMap(this.map)
        overlays.push(line)
      })
      // 高德边距顺序为上、下、左、右，左侧预留面板空间，避免路线节点被遮挡。
      if (overlays.length) this.map.setFitView(overlays, false, [70, 80, 470, 70])
    },
    applyMarkerDrag(node, event) {
      const lnglat = event && event.lnglat
      if (!lnglat) return
      const lng = typeof lnglat.getLng === 'function' ? lnglat.getLng() : lnglat.lng
      const lat = typeof lnglat.getLat === 'function' ? lnglat.getLat() : lnglat.lat
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
      node.lonLat = gcj02PointToWgs84LonLat(lng, lat)
      this.clearPlan()
      this.renderMap([])
      this.$message.info('节点坐标已更新，请重新规划路线')
    },
    clearPlan() {
      this.planRequestSequence++
      this.planning = false
      this.destroyDragRoute()
      this.planRoutes = []
      this.selectedPlanIndex = -1
      this.form.routeList = ''
      this.form.totalDileage = null
      this.form.totalDuration = null
      this.form.tolls = null
      this.form.tollDistance = null
      this.renderMap([])
    },
    submit() {
      const error = this.validatePlanNodes()
      if (error) { this.$message.warning(error); return }
      if (!this.form.routeList) { this.$message.warning('请先规划并选择路线'); return }
      if (this.embedded) {
        this.$emit('confirm', JSON.parse(JSON.stringify(this.form)))
        return
      }
      this.$message.success('已带回路线规划结果')
      this.returnToConfig()
    },
    mileageText(value) { return value === null || value === undefined || value === '' ? '-' : `${Number(value).toFixed(2)} 公里` },
    durationText(value) {
      if (!value) return '-'
      const seconds = Number(value)
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.ceil((seconds % 3600) / 60)
      return hours ? `${hours}小时${minutes}分` : `${minutes}分`
    },
    strategyText(value) { return { 0: '速度优先', 1: '最少费用', 2: '最短路径', 3: '规避拥堵' }[Number(value)] || '速度优先' },
    backToList() { this.$router.push({ name: 'LingDongRouteManageName' }) },
    returnToConfig() {
      if (this.embedded) { this.$emit('cancel'); return }
      sessionStorage.setItem(ROUTE_DRAFT_KEY, JSON.stringify(this.form))
      const route = this.form.id
        ? { name: 'LingDongRouteEditName', params: { id: this.form.id }, query: { fromPlanner: '1' } }
        : { name: 'LingDongRouteCreateName', query: { fromPlanner: '1' } }
      this.$router.push(route)
    }
  }
}
</script>

<style scoped>
.route-planner-page { position: relative; min-width: 760px; height: calc(100vh - 80px); min-height: 620px; overflow: hidden; background: #eef2f7; }
.route-planner-page--embedded { min-width: 0; height: 64vh; min-height: 480px; }.route-planner-map { width: 100%; height: 100%; }
.planner-panel { position: absolute; top: 12px; bottom: 12px; left: 12px; z-index: 5; width: 310px; overflow-y: auto; border-radius: 6px; background: rgba(255,255,255,.98); box-shadow: 0 3px 12px rgba(31,45,61,.18); transition: transform .2s ease; }
.planner-panel--collapsed { transform: translateX(-328px); }
.planner-panel__head { display: flex; align-items: center; justify-content: space-between; padding: 12px; border-bottom: 1px solid #e8edf5; color: #1f2d3d; font-size: 16px; }
.planner-panel__nodes { padding: 10px 10px 2px; }
.planner-node { display: flex; align-items: center; min-height: 46px; gap: 6px; margin-bottom: 7px; padding: 6px 8px; border: 1px solid #e3e8f1; border-radius: 6px; background: #fff; }
.planner-node__marker { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; flex: 0 0 26px; border-radius: 50%; color: #fff; background: #409eff; font-size: 13px; font-weight: 600; }
.planner-node--origin .planner-node__marker { background: #18a058; }.planner-node--target .planner-node__marker { background: #f56c6c; }
.planner-node__address { flex: 1; min-width: 0; overflow: hidden; color: #344054; font-size: 13px; white-space: nowrap; text-overflow: ellipsis; }
.planner-node__address--empty { color: #98a2b3; }
.planner-node__tools { display: grid; grid-template-columns: 16px 16px; flex: 0 0 34px; gap: 0 2px; color: #8490a3; font-size: 16px; }
.planner-node__tools i { cursor: pointer; }.planner-node__tools i:last-child { grid-row: span 2; align-self: center; }
.planner-node__tool--disabled { color: #d5dce8; cursor: not-allowed !important; }
.planner-panel__form { margin: 8px 10px 0; padding: 10px 0; border-top: 1px solid #edf0f5; }
.planner-route-warning { margin: 8px 10px; padding: 10px; border-radius: 4px; background: #fdf6ec; color: #b88230; font-size: 12px; line-height: 1.6; }
.planner-readonly-info { margin: 8px 10px 0; padding: 6px 0; border-top: 1px solid #edf0f5; }
.planner-readonly-info div { display: flex; justify-content: space-between; gap: 12px; padding: 6px 0; color: #667085; font-size: 13px; }
.planner-readonly-info strong { min-width: 0; overflow: hidden; color: #344054; font-weight: 500; text-align: right; white-space: nowrap; text-overflow: ellipsis; }
.planner-field { width: 100%; margin-bottom: 8px; }
.planner-panel__actions { display: flex; align-items: center; justify-content: space-between; }
.candidate-list { margin: 0 10px; padding: 10px 0; border-top: 1px solid #edf0f5; }
.candidate-title { margin-bottom: 6px; color: #276ef1; font-size: 14px; font-weight: 600; }
.candidate-item { display: grid; grid-template-columns: 1fr auto; width: 100%; gap: 4px 6px; margin-bottom: 6px; padding: 8px; border: 1px solid #dfe6f2; border-radius: 6px; background: #f7faff; color: #667085; font-size: 12px; text-align: left; cursor: pointer; }
.candidate-item__name { color: #344054; font-weight: 600; }.candidate-item__action { color: #276ef1; }.candidate-item--selected { border-color: #276ef1; background: #edf4ff; }
.planner-drag-action { margin-left: 10px; }.planner-drag-tip { padding: 4px 0 0; color: #667085; font-size: 11px; }
.planner-summary, .planner-empty { margin: 0 10px; padding: 10px; border-top: 1px solid #edf0f5; color: #667085; font-size: 12px; }.planner-summary { color: #529b2e; }
.planner-panel__footer { display: flex; justify-content: flex-end; gap: 6px; margin: 10px 10px 12px; padding-top: 10px; border-top: 1px solid #edf0f5; }
.planner-collapse { position: absolute; top: 50%; left: 322px; z-index: 6; width: 24px; height: 64px; transform: translateY(-50%); border: 1px solid #dce3ef; border-radius: 0 6px 6px 0; background: #fff; color: #276ef1; cursor: pointer; }.planner-collapse--closed { left: 0; }
.planner-back { position: absolute; top: 18px; right: 18px; z-index: 4; box-shadow: 0 1px 5px rgba(0,0,0,.12); }
.map-legend { position: absolute; right: 18px; bottom: 20px; z-index: 4; display: flex; gap: 14px; padding: 9px 12px; border-radius: 4px; background: rgba(255,255,255,.94); box-shadow: 0 1px 5px rgba(0,0,0,.12); color: #475467; font-size: 12px; }.map-legend span { display: inline-flex; align-items: center; gap: 4px; }.map-dot { width: 9px; height: 9px; border-radius: 50%; }.map-dot--start { background: #18a058; }.map-dot--waypoint { background: #409eff; }.map-dot--end { background: #f56c6c; }
</style>
